import { Link } from "react-router-dom";
import { Check, ShieldCheck, ArrowRight } from "lucide-react";
import Seo, { faqSchema, localBusinessSchema } from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Faq from "@/components/Faq";
import EnrollForm from "@/components/EnrollForm";
import { INSURERS, INSURANCE_FAQS } from "@/data/site";

const STEPS = [
  { n: "01", t: "Share your plan", d: "Tell us your insurer and member ID when you enroll — it takes two minutes." },
  { n: "02", t: "We verify your benefits", d: "We confirm your copay, coinsurance and deductible directly with your insurer, usually within one business day." },
  { n: "03", t: "You start with clarity", d: "You'll know exactly what to expect to pay before your first session — no surprises, no fine print." },
];

export default function Insurance() {
  const title = "Does Insurance Cover Therapy in Waterbury, CT? | Let Go Wellness";
  const meta = "Find out if your insurance covers therapy in Waterbury, CT. We accept Aetna, Cigna, UnitedHealthcare/Oxford & Anthem/BCBS, plus out-of-network superbills and self-pay. Free benefits check.";

  return (
    <>
      <Seo title={title} description={meta} path="/insurance" jsonLd={[localBusinessSchema, faqSchema(INSURANCE_FAQS)]} />

      <section className="border-b border-border">
        <div className="mx-auto max-w-3xl px-6 py-16 text-center md:py-24">
          <Reveal>
            <div className="overline mb-5 flex items-center justify-center gap-2"><ShieldCheck size={15} className="text-clay" /> Insurance & Fees</div>
            <h1 className="font-serif text-4xl leading-tight text-ink sm:text-5xl lg:text-6xl">Does insurance cover therapy in Waterbury, CT?</h1>
            <p className="mt-6 font-serif text-2xl italic text-clay">In most cases, yes — often for just a copay.</p>
            <p className="mx-auto mt-6 max-w-xl font-sans text-base leading-relaxed text-ink-soft">
              Cost should never be the reason you put off care. We accept most major insurance plans and verify your exact benefits before your first session — so you always know what to expect.
            </p>
            <Link to="/new-patient" data-testid="insurance-hero-cta" className="mt-8 inline-flex rounded-full bg-ink px-8 py-3.5 font-sans text-sm text-linen transition-colors hover:bg-ink/85">Get a free benefits check</Link>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border bg-white py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="overline mb-8 text-center">Plans we work with</div>
          <div className="grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2 md:grid-cols-3" data-testid="insurers-grid">
            {INSURERS.map((p) => (
              <div key={p} className="flex items-center gap-3 bg-white p-6 font-sans text-sm text-ink-soft">
                <Check size={16} className="shrink-0 text-clay" /> {p}
              </div>
            ))}
          </div>
          <p className="mt-6 text-center font-sans text-sm text-muted-foreground">
            Don't see your plan? Tell us who you're insured with — we'll check your out-of-network benefits and share self-pay options.
          </p>
        </div>
      </section>

      <section className="border-b border-border py-24">
        <div className="mx-auto max-w-5xl px-6">
          <h2 className="text-center font-serif text-3xl text-ink sm:text-4xl">How coverage works here</h2>
          <div className="mt-14 grid gap-px overflow-hidden border border-border bg-border md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white p-8">
                <div className="font-serif text-4xl italic text-clay">{s.n}</div>
                <h3 className="mt-4 font-serif text-xl text-ink">{s.t}</h3>
                <p className="mt-3 font-sans text-sm leading-relaxed text-ink-soft">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-white py-24">
        <div className="mx-auto grid max-w-5xl gap-14 px-6 md:grid-cols-2">
          <div className="prose-editorial">
            <h2 className="font-serif text-3xl text-ink">Out-of-network & superbills</h2>
            <p>If we're not in-network with your plan, you still have options. Many clients use out-of-network benefits to get reimbursed for a portion of each session.</p>
            <p>Each month we provide a detailed superbill you can submit to your insurer — we'll walk you through exactly how to do it.</p>
          </div>
          <div className="prose-editorial">
            <h2 className="font-serif text-3xl text-ink">Self-pay & sliding scale</h2>
            <p>Prefer to skip insurance, or don't have coverage? We keep self-pay simple and transparent, with no hidden fees.</p>
            <p>We also hold a limited number of reduced sliding-scale spots for those who need them. Just ask — we'll find a way to make care work for your budget.</p>
          </div>
        </div>
      </section>

      <Faq faqs={INSURANCE_FAQS} label="Insurance questions" />

      <section className="border-t border-border bg-white py-24">
        <div className="mx-auto max-w-3xl px-6">
          <div className="overline mb-4 text-center">No cost to find out</div>
          <h2 className="mb-4 text-center font-serif text-4xl text-ink sm:text-5xl">Let's <span className="italic text-clay">verify</span> your benefits.</h2>
          <p className="mx-auto mb-10 max-w-lg text-center font-sans text-sm text-ink-soft">Share your insurer below and we'll confirm your coverage — usually within one business day.</p>
          <EnrollForm />
        </div>
      </section>
    </>
  );
}
